import axiosInstance from './axiosInstance';
import { IResidentProvisionPayload } from '../interfaces/Resident/IResidentProvisionPayload';
import { IResidentListResponse } from '../interfaces/Resident/IResidentListResponse.ts';
import { ResidentFeature } from '../constants/ResidentFeature.ts';
import { IResidentBedsore } from '../interfaces/Resident/IResidentBedsore.ts';
import { IResidentListItem } from '../interfaces/Resident/IResidentListItem.ts';
import { IResidentUpdatePayload } from '../interfaces/Resident/IResidentUpdatePayload.ts';
import { IResidentCreatePayload } from '../interfaces/Resident/IResidentCreatePayload.ts';
import { ICreateResidentDiaperResponse } from '../interfaces/Diaper/ICreateResidentDiaperResponse.ts';
import { IListResidentReposIntervalsResponse } from '../interfaces/Resident/IResidentReposIntervalsResponse.ts';
import { IResidentReposInterval } from '../interfaces/Resident/IResidentReposInterval.ts';

class ResidentService {
  public async provisionResident(payload: IResidentProvisionPayload): Promise<any> {
    try {
      const response = await axiosInstance.post(`/residents/provision`, payload);
      return response.data;
    } catch (error) {
      throw new Error('Failed to provision resident');
    }
  }
  private static instance: ResidentService;

  private constructor() {}

  public static getInstance(): ResidentService {
    if (!ResidentService.instance) {
      ResidentService.instance = new ResidentService();
    }
    return ResidentService.instance;
  }

  public async getResidentBedsores(
    residentId: string
  ): Promise<Array<IResidentBedsore>> {
    try {
      const response = await axiosInstance.get<Array<IResidentBedsore>>(
        `/residents/${residentId}/bed_sores`
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch resident bedsores');
    }
  }

  public async createResidentBedsore(
    residentId: string,
    region: string,
    risk: string,
    note: string
  ): Promise<IResidentBedsore> {
    try {
      const response = await axiosInstance.post<IResidentBedsore>(
        `/residents/${residentId}/bed_sores`,
        { region, risk, note }
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to create resident bedsore');
    }
  }

  public async updateResidentBedsore(
    residentId: string,
    regionPath: string,
    region: string,
    risk: string,
    note: string
  ): Promise<IResidentBedsore> {
    try {
      const response = await axiosInstance.put<IResidentBedsore>(
        `/residents/${residentId}/bed_sores/${regionPath}`,
        { region, risk, note }
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to update resident bedsore');
    }
  }

  public async deleteResidentBedsore(
    residentId: string,
    region: string
  ): Promise<void> {
    try {
      await axiosInstance.delete<void>(
        `/residents/${residentId}/bed_sores/${region}`
      );
    } catch (error) {
      throw new Error('Failed to delete resident bedsore');
    }
  }

  public async getResidentList(params?: {
    page?: number;
    per_page?: number;
    query?: string;
    ignore_case?: boolean;
    orderBy?: string;
    sortOrder?: 'asc' | 'desc' | null;
    room?: string;
    station_id?: string;
    carehome_id?: string;
    organisation_id?: string;
    gender?: 'Woman' | 'Man' | 'Diverse' | null;
    rstate?: string | null;
    features?:
      | ResidentFeature.INCONTINENCE_MANAGEMENT_URINE
      | ResidentFeature.INCONTINENCE_MANAGEMENT_STOOL
      | ResidentFeature.REPOSITIONING_MANAGEMENT
      | ResidentFeature.SECURE_ZONE
      | ResidentFeature.FALL_MANAGEMENT;
    without_gateway?: boolean;
    show_archived?: boolean;
    is_active?: boolean;
  }): Promise<IResidentListResponse> {
    try {
      const response = await axiosInstance.get<IResidentListResponse>(
        `/residents`,
        { params }
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch resident data');
    }
  }

  public async getResident(residentId: string): Promise<IResidentListItem> {
    try {
      const response = await axiosInstance.get<IResidentListItem>(
        `/residents/${residentId}`
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch resident data');
    }
  }

  public async updateResident(
    residentId: string,
    residentData: IResidentUpdatePayload
  ): Promise<IResidentListItem> {
    try {
      const response = await axiosInstance.put<IResidentListItem>(
        `/residents/${residentId}`,
        residentData
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to update resident data');
    }
  }

  public async createResident(
    residentData: IResidentCreatePayload
  ): Promise<IResidentListItem> {
    try {
      const response = await axiosInstance.post<IResidentListItem>(
        `/residents`,
        residentData
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to create resident');
    }
  }

  public async updateResidentFeatures(
    residentId: string,
    features: string[]
  ): Promise<void> {
    try {
      await axiosInstance.patch(`/residents/${residentId}/features`, {
        features,
      });
    } catch (error) {
      throw new Error('Failed to update resident features');
    }
  }

  public async createResidentDiaper(
    residentId: string,
    diaperData: {
      carehome_diaper_id: string;
      usage: 'Day' | 'Night';
      is_active: boolean;
      usage_amount: number;
    }
  ): Promise<ICreateResidentDiaperResponse> {
    try {
      const response = await axiosInstance.post<ICreateResidentDiaperResponse>(
        `/residents/${residentId}/diapers`,
        diaperData
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to create resident diaper');
    }
  }

  public async deleteResidentDiaper(
    residentId: string,
    diaperId: string
  ): Promise<void> {
    try {
      await axiosInstance.delete(
        `/residents/${residentId}/diapers/${diaperId}`
      );
    } catch (error) {
      throw new Error('Failed to delete resident diaper');
    }
  }

  public async createResidentReposInterval(
    residentId: string,
    intervalData: {
      resident_id: string;
      type:
        | 'General Day'
        | 'General Night'
        | 'Supine'
        | 'Prone'
        | 'Right Side'
        | 'Left Side'
        | 'Sitting'
        | 'Wheelchair';
      value: string; // duration,
      start?: string; // ISO 8601 date-time string
      end?: string; // ISO 8601 date-time string
    }
  ): Promise<void> {
    try {
      await axiosInstance.post<void>(
        `/residents/${residentId}/repos_intervals`,
        intervalData
      );
    } catch (error) {
      throw new Error('Failed to create resident repositioning interval');
    }
  }

  public async updateResidentReposInterval(
    residentId: string,
    resident_repos_interval_id: string,
    intervalData: {
      resident_id: string;
      type:
        | 'General Day'
        | 'General Night'
        | 'Supine'
        | 'Prone'
        | 'Right Side'
        | 'Left Side'
        | 'Sitting'
        | 'Wheelchair';
      value: string; // duration
      start?: string; // ISO 8601 date-time string
      end?: string; // ISO 8601 date-time string
    }
  ): Promise<void> {
    try {
      await axiosInstance.put<void>(
        `/residents/${residentId}/repos_intervals/${resident_repos_interval_id}`,
        intervalData
      );
    } catch (error) {
      throw new Error('Failed to update resident repositioning interval');
    }
  }

  public async listResidentReposIntervals(
    residentId: string,
    params?: {
      page?: number;
      per_page?: number;
      query?: string;
      ignore_case?: boolean;
      orderBy?: string;
      sortOrder?: 'asc' | 'desc' | null;
      carehome_id?: string;
      is_active?: boolean;
    }
  ): Promise<IListResidentReposIntervalsResponse> {
    try {
      const response = await axiosInstance.get<{
        items: IResidentReposInterval[];
        limit: number;
        offset: number;
        total: number;
      }>(`/residents/${residentId}/repos_intervals`, { params });
      return response.data;
    } catch (error) {
      throw new Error('Failed to list resident repositioning intervals');
    }
  }

  public async deleteResidentReposInterval(
    residentId: string,
    residentReposIntervalId: string
  ): Promise<void> {
    try {
      await axiosInstance.delete<void>(
        `/residents/${residentId}/repos_intervals/${residentReposIntervalId}`
      );
    } catch (error) {
      throw new Error('Failed to delete resident repositioning interval');
    }
  }
}

export default ResidentService.getInstance();
